import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AncillaryStatusTypeEnum,
  AncillaryTypeEnum,
  AncillaryUpsellNode,
  PortalAncillaryBundle,
  PortalAncillaryNode,
  PortalOrderNode,
} from '@app/services/api/api.types';

import { catchError, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { PortalRoutes } from '@app/app-routing.module';
import { ApiProtectedService } from '@app/services/api/api.protected.service';
import { ApiService } from '@app/services/api/api.service';
import { VerificationModalService } from '@app/services/verification-modal.service';
import { PopupsService } from '@voyagu/portal-design-system';
import { Tracking } from '@app/services/tracking/tracking.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StateService } from '@app/services/state/state.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
@Component({
  selector: 'trip-page',
  templateUrl: './trip-page.component.html',
  styleUrls: ['./trip-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TripPageComponent {
  PortalRoutes = PortalRoutes;

  private location = inject(Location);

  apiProtectedService = inject(ApiProtectedService);
  apiService = inject(ApiService);
  router = inject(Router);
  activateRoute = inject(ActivatedRoute);
  verificationService = inject(VerificationModalService);
  popupsService = inject(PopupsService);
  stateService = inject(StateService);

  isLoading = true;

  disruptionProtection: number = 0;
  cancelForAnyReason: number = 0;
  bundle?: PortalAncillaryBundle;

  trip$: Observable<PortalOrderNode | null> = this.activateRoute.paramMap.pipe(
    switchMap((data) => {
      const id = data.get(PortalRoutes.TripsParam);

      if (id) {
        return this.apiProtectedService.portalAuthorizedOrder(id).pipe(
          map((response) => {
            const authorizedOrder = response.data?.portalAuthorizedOrder;
            const verification = authorizedOrder?.verification;

            if (verification?.required) {
              this.verificationService.showModal(
                verification,
                this.stateService.getState().portalLink,
              );
            }

            if (verification) {
              Tracking.patch({ verificationRequired: verification.required });
            }

            const order = authorizedOrder?.order;

            if (!order) {
              return null;
            }

            const purchasedAncillaries = order.ancillaries.filter(
              (ancillary) => ancillary.status === AncillaryStatusTypeEnum.PURCHASED,
            );
            this.bundle = order.ancillaryBundle;

            this.disruptionProtection = this.getAncillaryPrice(
              purchasedAncillaries,
              AncillaryTypeEnum.DISRUPTION_PROTECTION,
            );

            this.cancelForAnyReason = this.getAncillaryPrice(
              purchasedAncillaries,
              AncillaryTypeEnum.CANCEL_FOR_ANY_REASON,
            );

            return order;
          }),
          catchError(() => {
            Tracking.track(PortalTrackingEvents.portalAuthorizedOrderRequestError);
            return of(null);
          }),
        );
      }

      void this.router.navigate([``], { queryParamsHandling: 'merge' });

      return of(null);
    }),
    tap(() => (this.isLoading = false)),
    shareReplay(1),
    takeUntilDestroyed(),
  );

  tripUpsellBanner$: Observable<Partial<AncillaryUpsellNode> | null> = this.trip$.pipe(
    switchMap((trip) => {
      const tripPortalLink = trip?.tripPortalLink;

      if (!tripPortalLink) {
        return of(null);
      }

      return this.apiService.portalTripAncillaryUpsellBanner({ tripPortalLink }).pipe(
        map(({ data }) => data.portalTripAncillaryUpsell),
        catchError(() => of(null)),
      );
    }),
  );

  onPrintItinerary() {
    window.print();
  }

  goBack() {
    this.location.back();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onCancelFlight() {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChangesDates() {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onDownloadReceipt() {}

  getAncillaryPrice(ancillaries: PortalAncillaryNode[], type: AncillaryTypeEnum): number {
    return +(ancillaries.find((ancillary) => ancillary.type === type)?.price ?? 0);
  }
}
